import './ExceptionStateNotices.css';
import {HolmesJrGuide} from './HolmesJrGuide';

/**
 * There's no real session in this synthetic module -- nothing here expires
 * on a timer. This is the presentational contract for what a real session
 * timeout should show: a plain explanation, not a silent redirect or a raw
 * 401. Real integration triggers this by rendering it in place of a
 * protected screen when the auth layer reports an expired session (Codex's
 * lane), same as PermissionDeniedNotice below.
 */
type SessionExpiredNoticeProps = {
  /** What the customer was doing when the session expired, e.g. "the request you were filling out". Shown so the message doesn't feel generic. */
  context?: string;
  signInHref?: string;
  /**
   * First real caller (FenzSafeWorkspace.tsx, Issue #114 session-expired recovery hardening):
   * a genuine real-backend session expiry, not the walkthrough demo (ExceptionStatesWalkthrough.tsx)
   * this component was originally built for. Swaps the "Local synthetic module" banner -- which
   * would otherwise falsely claim nothing here tracks a real session -- for the same "real account"
   * phrasing FenzSafeApp.tsx/FenzSafeShareView.tsx already use elsewhere in this real mount.
   * Defaults to false so every existing (synthetic/demo) caller is unaffected.
   */
  isRealSession?: boolean;
};

export function SessionExpiredNotice({context, signInHref = '/sign-in', isRealSession = false}: SessionExpiredNoticeProps) {
  return (
    <section aria-label="Session expired" className="esn-root">
      <HolmesJrGuide pose="guide" tip="Sessions expire after a period of inactivity to keep your account secure — nothing you entered was lost on our end." />
      <p className="esn-flag" role="status">
        {isRealSession ? (
          <>
            <strong>Real account</strong>
            <span>This reflects your actual sign-in state — you were genuinely signed out.</span>
          </>
        ) : (
          <>
            <strong>Local synthetic module</strong>
            <span>This screen is a fixed example — nothing here tracks a real session or timer.</span>
          </>
        )}
      </p>
      <div className="esn-card">
        <span className="esn-icon is-session" aria-hidden="true">
          🔒
        </span>
        <p className="esn-eyebrow">SESSION EXPIRED</p>
        <h1>You've been signed out</h1>
        <p>For your security, you were signed out after a period of inactivity{context ? ` while working on ${context}` : ''}.</p>
        <div className="esn-actions">
          <a className="esn-primary" href={signInHref}>
            Sign in again
          </a>
        </div>
      </div>
    </section>
  );
}

type PermissionDeniedNoticeProps = {
  /** What was being accessed, e.g. "Team & Invitations" or "this company's Mission Control". */
  resourceLabel: string;
  /** Why it's out of reach for this account, in plain language, e.g. "This section is only available to company administrators." */
  reason?: string;
  homeHref?: string;
  homeLabel?: string;
};

export function PermissionDeniedNotice({resourceLabel, reason, homeHref = '/', homeLabel = 'Back to your dashboard'}: PermissionDeniedNoticeProps) {
  return (
    <section aria-label="Access denied" className="esn-root">
      <HolmesJrGuide pose="guide" tip="Permissions are tied to your role — an administrator on your team can grant broader access if you need it." />
      <p className="esn-flag" role="status">
        <strong>Local synthetic module</strong>
        <span>This screen is a fixed example — real role checks happen in the authenticated backend, not here.</span>
      </p>
      <div className="esn-card">
        <span className="esn-icon is-permission" aria-hidden="true">
          ⛔
        </span>
        <p className="esn-eyebrow">ACCESS DENIED</p>
        <h1>You don't have access to this</h1>
        <p>Your account doesn't have permission to view {resourceLabel}.</p>
        {reason && <p className="esn-detail">{reason}</p>}
        <div className="esn-actions">
          <a className="esn-primary" href={homeHref}>
            {homeLabel}
          </a>
        </div>
      </div>
    </section>
  );
}
